/**
 * Generic DataTable component
 * Provides sorting, pagination (8 rows/page), loading/error/empty states,
 * optional footer totals row, and CSV/Excel/PNG export.
 */

import { useState, useMemo, useEffect, Fragment } from 'react'
import type { ReactNode } from 'react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { ChartExportMenu } from '@/components/dashboard/ChartExportMenu'
import { exportToCSV, exportToExcel, exportToExcelOutlined, exportToPNG, generateFilename } from '@/lib/utils/export-utils'
import type { OutlinedExportRow } from '@/lib/utils/export-utils'
import { getAriaSortValue } from '@/components/ui/sort-icon'
import { useToast } from '@/hooks/use-toast'

/**
 * Passed as a second argument to every column's render. Lets a column draw its own expand control
 * (a chevron in the name cell, say) without the table needing a dedicated expander column, which
 * would shift every header and throw off footerColSpan.
 */
export interface RowRenderContext {
  /** True when this row's detail is currently open */
  isExpanded: boolean
  /** True when expandedRowRender is supplied AND isRowExpandable allows this row */
  canExpand: boolean
  /** Toggles this row's detail. Call e.stopPropagation() first if the row also has onRowClick. */
  toggleExpanded: () => void
}

export interface ColumnDef<T> {
  key: string
  header: string
  align?: 'left' | 'right'
  sortable?: boolean
  /** Field to sort by — if omitted, column is not sortable even if sortable=true */
  sortKey?: keyof T
  render: (row: T, ctx: RowRenderContext) => ReactNode
  footerRender?: () => ReactNode
  headerClassName?: string
  className?: string
}

export interface DataTableExportConfig {
  filenamePrefix: string
  sheetName: string
  buildExportData: () => Record<string, unknown>[]
  /**
   * Excel rows with outline levels, for a table whose rows summarise other rows. Supply this and
   * the .xlsx gets collapsible groups instead of the flat buildExportData set; omit it and Excel is
   * unchanged. May fetch, so it is async.
   */
  buildExcelRows?: () => Promise<OutlinedExportRow[]>
  /**
   * CSV rows, when they differ from buildExportData. CSV has no notion of a group, so a table that
   * summarises rows exports the detail here rather than a summary that cannot be expanded. May
   * fetch, so it is async.
   */
  buildCsvRows?: () => Promise<Record<string, unknown>[]>
  /** Filename prefix for CSV when its contents differ from Excel's. Defaults to filenamePrefix. */
  csvFilenamePrefix?: string
}

export interface DataTableProps<T> {
  id?: string
  title: string
  description?: string | ReactNode
  columns: ColumnDef<T>[]
  data: T[] | null
  pageSize?: number
  getRowKey: (row: T, index: number) => string | number
  isLoading?: boolean
  error?: string | null
  emptyMessage?: string
  onRowClick?: (row: T) => void
  rowClassName?: (row: T) => string
  /** Whether to render a tfoot row using each column's footerRender */
  showFooter?: boolean
  /** Number of leading columns collapsed into the "TOTAL" label cell */
  footerColSpan?: number
  exportConfig?: DataTableExportConfig
  /**
   * Renders a full-width detail row beneath an expanded row. Omit and the table behaves exactly as
   * before — no expansion state, no extra markup.
   */
  expandedRowRender?: (row: T) => ReactNode
  /** Which rows may expand. Defaults to all rows when expandedRowRender is supplied. */
  isRowExpandable?: (row: T) => boolean
  /**
   * Controls rendered in the card header, beside the export menu. For anything that changes what
   * THIS table shows — putting such a control in a page-level filter bar implies it affects the
   * whole page.
   */
  headerActions?: ReactNode
}

const DEFAULT_PAGE_SIZE = 8

export function DataTable<T>({
  id,
  title,
  description,
  columns,
  data,
  pageSize = DEFAULT_PAGE_SIZE,
  getRowKey,
  isLoading,
  error,
  emptyMessage = 'No data available',
  onRowClick,
  rowClassName,
  showFooter = false,
  footerColSpan = 1,
  exportConfig,
  expandedRowRender,
  isRowExpandable,
  headerActions,
}: DataTableProps<T>) {
  const [sortKey, setSortKey] = useState<keyof T | null>(null)
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const [page, setPage] = useState(0)
  const [expandedKeys, setExpandedKeys] = useState<Set<string | number>>(new Set())
  const { toast } = useToast()

  // Reset to page 0 when data changes. Expansion is cleared with it — keys refer to rows that may
  // no longer exist, and a detail row left open across a filter change would describe stale data.
  useEffect(() => {
    setPage(0)
    setExpandedKeys(new Set())
  }, [data])

  const toggleExpanded = (key: string | number) => {
    setExpandedKeys(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const handleSort = (col: ColumnDef<T>) => {
    if (!col.sortable || !col.sortKey) return
    if (col.sortKey === sortKey) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(col.sortKey)
      setSortDir('asc')
    }
    setPage(0)
  }

  const sorted = useMemo(() => {
    if (!data) return []
    if (!sortKey) return [...data]
    return [...data].sort((a, b) => {
      const av = a[sortKey]
      const bv = b[sortKey]
      if (av === null || av === undefined) return 1
      if (bv === null || bv === undefined) return -1
      if (typeof av === 'string' && typeof bv === 'string') {
        return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av)
      }
      const an = av as number
      const bn = bv as number
      return sortDir === 'asc' ? an - bn : bn - an
    })
  }, [data, sortKey, sortDir])

  const totalPages = Math.ceil(sorted.length / pageSize)
  const pageRows = sorted.slice(page * pageSize, (page + 1) * pageSize)

  // Export handlers. The optional builders may fetch, which the old synchronous buildExportData
  // never did - so there is now a real failure path (an expired token, a slow SP) with nothing on
  // screen to show for it. A download that silently never arrives reads as a blocked popup, so say
  // so rather than leave it to the console.
  const exportFailed = (format: string, err: unknown) => {
    // Detail stays in the console. api.ts builds its errors from the server's own response body
    // (`errorData.message`), so err.message can carry backend or SQL text straight from a failing
    // stored procedure - not something to paste into a toast.
    console.error(`Failed to build ${format} export:`, err)
    toast({
      title: `${format} Export Failed`,
      description: 'Could not build the export. Please try again.',
      variant: 'destructive',
    })
  }

  const handleExportCSV = async () => {
    if (!exportConfig) return
    const filename = generateFilename(
      exportConfig.csvFilenamePrefix ?? exportConfig.filenamePrefix,
      'portfolio',
      'csv'
    )
    try {
      const rows = exportConfig.buildCsvRows
        ? await exportConfig.buildCsvRows()
        : exportConfig.buildExportData()
      exportToCSV(rows, filename)
    } catch (err) {
      exportFailed('CSV', err)
    }
  }
  const handleExportExcel = async () => {
    if (!exportConfig) return
    const filename = generateFilename(exportConfig.filenamePrefix, 'portfolio', 'xlsx')
    try {
      if (exportConfig.buildExcelRows) {
        exportToExcelOutlined(await exportConfig.buildExcelRows(), filename, exportConfig.sheetName)
      } else {
        exportToExcel(exportConfig.buildExportData(), filename, exportConfig.sheetName)
      }
    } catch (err) {
      exportFailed('Excel', err)
    }
  }
  const handleExportPNG = () => {
    if (!id || !exportConfig) return
    exportToPNG(id, generateFilename(exportConfig.filenamePrefix, 'portfolio', 'png'))
  }

  const SortIcon = ({ col }: { col: ColumnDef<T> }) => {
    if (!col.sortable || !col.sortKey) return null
    if (col.sortKey !== sortKey) return <span className="ml-1 opacity-30">↕</span>
    return <span className="ml-1">{sortDir === 'asc' ? '↑' : '↓'}</span>
  }

  const thClass = (col: ColumnDef<T>) =>
    cn(
      'py-3 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap',
      col.align === 'right' ? 'text-right' : 'text-left',
      col.headerClassName,
    )

  // Same header shape as the success state. headerActions in particular MUST render here: a control
  // that changes what this table shows generally triggers a fetch, so omitting it means the control
  // unmounts under the cursor the moment it is used - and if that fetch errors, the only way to undo
  // the change is a page reload, because the control never comes back. The export menu is
  // deliberately left out; there is nothing to export in these states.
  const loadingOrEmpty = (content: ReactNode) => (
    <Card>
      <CardHeader className="py-3 px-4">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle>{title}</CardTitle>
            {description && <CardDescription>{description}</CardDescription>}
          </div>
          {headerActions && <div className="flex items-center gap-3">{headerActions}</div>}
        </div>
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  )

  if (isLoading) {
    return loadingOrEmpty(
      <div className="flex items-center justify-center py-8">
        <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
      </div>
    )
  }

  if (error) {
    return loadingOrEmpty(
      <p className="text-sm text-red-600 text-center py-8">{error}</p>
    )
  }

  if (!data || data.length === 0) {
    return loadingOrEmpty(
      <p className="text-sm text-muted-foreground text-center py-8">{emptyMessage}</p>
    )
  }

  return (
    <Card id={id}>
      <CardHeader className="py-3 px-4">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle>{title}</CardTitle>
            {description && (
              <CardDescription>{description}</CardDescription>
            )}
          </div>
          <div className="flex items-center gap-3">
            {headerActions}
            {exportConfig && (
              <ChartExportMenu
                onExportCSV={handleExportCSV}
                onExportExcel={handleExportExcel}
                onExportPNG={handleExportPNG}
              />
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/30 text-left">
                {columns.map(col => (
                  <th
                    key={col.key}
                    className={thClass(col)}
                    aria-sort={
                      col.sortable && col.sortKey
                        ? getAriaSortValue(String(col.sortKey), sortKey ? String(sortKey) : undefined, sortDir)
                        : undefined
                    }
                  >
                    {col.sortable && col.sortKey ? (
                      <button
                        type="button"
                        className={cn('flex items-center gap-1 cursor-pointer select-none hover:text-foreground transition-colors w-full', col.align === 'right' && 'justify-end')}
                        onClick={() => handleSort(col)}
                      >
                        {col.header}
                        <SortIcon col={col} />
                      </button>
                    ) : (
                      col.header
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageRows.map((row, idx) => {
                const rowKey = getRowKey(row, idx)
                const canExpand = !!expandedRowRender && (isRowExpandable?.(row) ?? true)
                const isExpanded = canExpand && expandedKeys.has(rowKey)
                const ctx: RowRenderContext = {
                  isExpanded,
                  canExpand,
                  toggleExpanded: () => toggleExpanded(rowKey),
                }

                return (
                  <Fragment key={rowKey}>
                    <tr
                      onClick={() => onRowClick?.(row)}
                      tabIndex={onRowClick ? 0 : undefined}
                      role={onRowClick ? 'link' : undefined}
                      onKeyDown={onRowClick ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          onRowClick(row)
                        }
                      } : undefined}
                      className={cn(
                        'border-b transition-colors',
                        onRowClick && 'cursor-pointer',
                        rowClassName?.(row) ?? 'hover:bg-muted/30',
                      )}
                    >
                      {columns.map(col => (
                        <td
                          key={col.key}
                          className={cn(
                            'py-3 px-3 text-sm',
                            col.align === 'right' && 'text-right tabular-nums',
                            col.className,
                          )}
                        >
                          {col.render(row, ctx)}
                        </td>
                      ))}
                    </tr>
                    {isExpanded && (
                      <tr className="border-b bg-muted/20">
                        <td colSpan={columns.length} className="p-0">
                          {expandedRowRender(row)}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
            </tbody>
            {showFooter && (
              <tfoot>
                <tr className="border-t-2 bg-muted/50 font-semibold">
                  <td className="py-3 px-3 text-sm whitespace-nowrap" colSpan={footerColSpan}>
                    TOTAL
                  </td>
                  {columns.slice(footerColSpan).map(col => (
                    <td
                      key={col.key}
                      className={cn(
                        'py-3 px-3 text-sm whitespace-nowrap',
                        col.align === 'right' && 'text-right tabular-nums',
                        col.className,
                      )}
                    >
                      {col.footerRender?.()}
                    </td>
                  ))}
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <p className="text-xs text-muted-foreground">
              Showing {page * pageSize + 1}–{Math.min((page + 1) * pageSize, sorted.length)} of {sorted.length}
            </p>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                ‹ Prev
              </Button>
              {Array.from({ length: totalPages }, (_, i) => (
                <Button
                  key={i}
                  variant={i === page ? 'default' : 'outline'}
                  size="sm"
                  className="h-7 w-7 p-0 text-xs"
                  onClick={() => setPage(i)}
                >
                  {i + 1}
                </Button>
              ))}
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page === totalPages - 1}
              >
                Next ›
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
